import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as ordersApi from '../api/ordersApi';
import { PaymentBadge, StatusBadge } from '../components/StatusBadge';
import { Card, EmptyState, ErrorBanner, Modal, Pagination, Spinner, TableSkeleton } from '../components/ui';
import { IconDownload, IconSearch, IconTruck } from '../components/icons';
import { isAwaitingPayment, Order, ORDER_STATUSES } from '../types/order';
import { capitalize, formatDate, formatINR, shortId } from '../utils/format';

export default function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const search = searchParams.get('q') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const userId = searchParams.get('user') ?? '';

  const [orders, setOrders] = useState<Order[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(search);

  // Bulk shipping (Shiprocket). Only paid orders with no shipment yet are
  // selectable; each selected order gets its own AWB via auto-assign.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<ordersApi.BulkShipResult | null>(null);
  const [bulkError, setBulkError] = useState('');
  const [downloadingLabels, setDownloadingLabels] = useState(false);
  const [downloadingSelected, setDownloadingSelected] = useState(false);
  const [downloadingInvoices, setDownloadingInvoices] = useState(false);
  const [barError, setBarError] = useState('');
  const [barNotice, setBarNotice] = useState('');

  // Paid orders with no shipment yet → selectable for bulk shipping.
  const isShippable = (o: Order) =>
    (o.payment_method === 'cod' || o.payment_status === 'paid') &&
    !o.awb_number &&
    !o.shipping_status &&
    o.fulfillment_type !== 'self_ship' && // delivered by hand — never gets a courier
    !['cancelled', 'refunded'].includes(o.status);

  // Orders that already have a shipment → selectable for bulk label download.
  const hasShipment = (o: Order) => Boolean(o.shipping_status) || Boolean(o.awb_number);

  // Paid orders → selectable for bulk GST invoice download. Money actually
  // received, so COD (which stays pending until collection) doesn't qualify.
  const isInvoiceable = (o: Order) => o.payment_status === 'paid';

  // A row is selectable if there is any bulk action it can take part in:
  // shipping, label download, or invoice download.
  const isSelectable = (o: Order) => isShippable(o) || hasShipment(o) || isInvoiceable(o);

  const setParam = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!('page' in updates)) next.delete('page'); // filter changes reset to page 1
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await ordersApi.getOrders({
        status: status || undefined,
        page,
        limit: 20,
        search: search || undefined,
        from: from || undefined,
        to: to || undefined,
        user_id: userId || undefined,
      });
      setOrders(result.items);
      setTotalPages(result.totalPages);
      setTotal(result.total);
      setSelected(new Set()); // reset selection on any filter/page change
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [status, page, search, from, to, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setParam({ q: searchInput.trim() });
  };

  const eligibleIds = orders.filter(isSelectable).map((o) => o.id);
  const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));

  // Split the current selection into its actionable groups.
  const selectedOrders = orders.filter((o) => selected.has(o.id));
  const selectedShippableIds = selectedOrders.filter(isShippable).map((o) => o.id);
  const selectedShipmentIds = selectedOrders.filter(hasShipment).map((o) => o.id);
  // Invoices are all-or-nothing on purpose: a batch that quietly dropped the one
  // unpaid order in it would read as complete when it wasn't.
  const allSelectedPaid = selectedOrders.length > 0 && selectedOrders.every(isInvoiceable);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(eligibleIds));

  const runBulkShip = async () => {
    if (selectedShippableIds.length === 0) return;
    setBulkBusy(true);
    setBulkError('');
    try {
      const res = await ordersApi.bulkShip(selectedShippableIds, null);
      setBulkResult(res);
      await load();
    } catch (err) {
      setBulkError(apiErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  };

  // Combined PDF of all labels across the selected already-shipped orders.
  const downloadSelectedLabels = async () => {
    if (selectedShipmentIds.length === 0) return;
    setDownloadingSelected(true);
    setBarError('');
    try {
      const { label_url } = await ordersApi.bulkLabelsByOrders(selectedShipmentIds);
      if (label_url) window.open(label_url, '_blank', 'noopener');
      else setBarError('No label URL was returned by Shiprocket.');
    } catch (err) {
      setBarError(apiErrorMessage(err));
    } finally {
      setDownloadingSelected(false);
    }
  };

  /**
   * One PDF holding a 4×6 GST invoice per order, one per page — the "print the
   * batch" action. `selector` is either the explicit selection or
   * status=formulated, which lets the server pick up every paid formulated order
   * rather than only the ones on this page.
   */
  const downloadInvoices = async (selector: { orderIds: string[] } | { status: string }) => {
    setDownloadingInvoices(true);
    setBarError('');
    setBarNotice('');
    try {
      const { count, skipped } = await ordersApi.downloadBulkInvoices(selector);
      // Say what the file actually holds. A short PDF that reports nothing is
      // indistinguishable from a complete one until someone counts the pages.
      setBarNotice(
        skipped > 0
          ? `Downloaded ${count} invoice${count === 1 ? '' : 's'} — ${skipped} order(s) left out (unpaid, or beyond the 50-per-download cap).`
          : `Downloaded ${count} invoice${count === 1 ? '' : 's'}.`
      );
    } catch (err) {
      setBarError(err instanceof Error ? err.message : apiErrorMessage(err));
    } finally {
      setDownloadingInvoices(false);
    }
  };

  // Combined PDF of all labels from the bulk-ship run (opens in a new tab).
  const downloadAllLabels = async () => {
    if (!bulkResult) return;
    const ids = bulkResult.results
      .filter((r) => r.success && r.shiprocket_shipment_id)
      .map((r) => r.shiprocket_shipment_id as string);
    if (ids.length === 0) return;
    setDownloadingLabels(true);
    setBulkError('');
    try {
      const { label_url } = await ordersApi.bulkLabels(ids);
      if (label_url) window.open(label_url, '_blank', 'noopener');
      else setBulkError('No label URL was returned by Shiprocket.');
    } catch (err) {
      setBulkError(apiErrorMessage(err));
    } finally {
      setDownloadingLabels(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Orders</h1>
        <p className="text-sm text-sub">
          {total > 0 ? `${total} order${total === 1 ? '' : 's'}` : 'Manage customer orders'}
          {userId && ' · filtered by user'}
        </p>
      </div>

      {/* Filter bar */}
      <div className="card flex flex-wrap items-end gap-3 p-4">
        <div className="w-full sm:w-44">
          <label className="label">Status</label>
          <select className="input" value={status} onChange={(e) => setParam({ status: e.target.value })}>
            <option value="">All</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {capitalize(s.replace(/_/g, ' '))}
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={handleSearch} className="w-full min-w-[180px] flex-1 sm:w-auto">
          <label className="label">Search order ID</label>
          <div className="relative">
            <input
              className="input pr-10"
              placeholder="Order ID…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sub transition-colors hover:text-ink"
              aria-label="Search"
            >
              <IconSearch className="h-4 w-4" />
            </button>
          </div>
        </form>

        <div className="w-full sm:w-40">
          <label className="label">From</label>
          <input type="date" className="input" value={from} onChange={(e) => setParam({ from: e.target.value })} />
        </div>
        <div className="w-full sm:w-40">
          <label className="label">To</label>
          <input type="date" className="input" value={to} onChange={(e) => setParam({ to: e.target.value })} />
        </div>

        {/* Quick filter for the dispatch batch — the stage the admin returns to
            several times a day, so it gets a button rather than a dropdown hunt.
            Pressing it again clears the filter. */}
        <button
          className={status === 'formulated' ? 'btn-gold' : 'btn-outline'}
          onClick={() => setParam({ status: status === 'formulated' ? '' : 'formulated' })}
          aria-pressed={status === 'formulated'}
        >
          Formulated
        </button>

        {(status || search || from || to || userId) && (
          <button
            className="btn-ghost"
            onClick={() => {
              setSearchInput('');
              setSearchParams(new URLSearchParams());
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* One-click "print today's batch". Server-side selection, so it covers
          every paid formulated order rather than only this page's rows. */}
      {status === 'formulated' && (
        <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="text-sm">
            <p className="font-medium">Dispatch batch</p>
            <p className="text-xs text-sub">
              One PDF, one 4×6 invoice per page — up to 50 orders per download.
            </p>
          </div>
          <button
            className="btn-gold"
            onClick={() => downloadInvoices({ status: 'formulated' })}
            disabled={downloadingInvoices}
          >
            {downloadingInvoices ? <Spinner /> : <IconDownload className="h-4 w-4" />}
            Download All Formulated Invoices
          </button>
        </div>
      )}

      {error && <ErrorBanner message={error} onRetry={load} />}

      {/* Bulk actions bar — ship the unshipped selection and/or download labels
          for the already-shipped selection. */}
      {selected.size > 0 && (
        <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="text-sm">
            <span className="font-medium">
              {selected.size} order{selected.size === 1 ? '' : 's'} selected
            </span>
            {barError && <span className="ml-3 text-bad">{barError}</span>}
            {barNotice && !barError && <span className="ml-3 text-ok">{barNotice}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-ghost" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
            <button
              className="btn-outline"
              onClick={() => downloadInvoices({ orderIds: Array.from(selected) })}
              disabled={!allSelectedPaid || downloadingInvoices}
              title={
                allSelectedPaid
                  ? undefined
                  : 'Every selected order must be paid before invoices can be issued.'
              }
            >
              {downloadingInvoices ? <Spinner /> : <IconDownload className="h-4 w-4" />}
              Download Invoices
            </button>
            {selectedShipmentIds.length > 0 && (
              <button
                className="btn-outline"
                onClick={downloadSelectedLabels}
                disabled={downloadingSelected}
              >
                {downloadingSelected && <Spinner />}
                Download labels ({selectedShipmentIds.length})
              </button>
            )}
            {selectedShippableIds.length > 0 && (
              <button
                className="btn-gold"
                onClick={() => {
                  setBulkResult(null);
                  setBulkError('');
                  setBulkOpen(true);
                }}
              >
                <IconTruck className="h-4 w-4" />
                Ship selected ({selectedShippableIds.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Orders table */}
      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={8} />
        ) : orders.length === 0 ? (
          <EmptyState message="No orders found" hint="Try adjusting the filters" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-gold"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={eligibleIds.length === 0}
                        aria-label="Select all actionable orders on this page"
                      />
                    </th>
                    <th className="th">Order ID</th>
                    <th className="th">Customer</th>
                    <th className="th">Date</th>
                    <th className="th">Amount (incl. GST)</th>
                    <th className="th">Status</th>
                    <th className="th">Payment</th>
                    <th className="th">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {orders.map((o) => (
                    <tr key={o.id} className="transition-colors hover:bg-edge/20">
                      <td className="td">
                        {isSelectable(o) && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-gold"
                            checked={selected.has(o.id)}
                            onChange={() => toggle(o.id)}
                            aria-label={`Select order ${shortId(o.id)}`}
                          />
                        )}
                      </td>
                      <td className="td font-medium">
                        {shortId(o.id)}
                        {(o.order_type === 'formulation' || o.has_custom) && (
                          <span className="mt-1 block text-xs font-normal text-gold">
                            Custom formulation
                          </span>
                        )}
                        {o.fulfillment_type === 'self_ship' && (
                          <span className="mt-1 inline-block rounded-full border border-edge px-2 py-0.5 text-[11px] font-medium text-sub">
                            Self-ship
                          </span>
                        )}
                      </td>
                      <td className="td text-sub">
                        {o.customer_name ?? o.shipping_address?.full_name ?? '—'}
                      </td>
                      <td className="td text-sub">{formatDate(o.created_at)}</td>
                      <td className="td font-medium">{formatINR(o.total)}</td>
                      <td className="td">
                        <StatusBadge
                          status={o.status}
                          label={isAwaitingPayment(o) ? 'Awaiting payment' : undefined}
                        />
                      </td>
                      <td className="td">
                        <PaymentBadge status={o.payment_status} />
                      </td>
                      <td className="td">
                        <Link to={`/orders/${o.id}`} className="btn-outline !px-3 !py-1.5 !text-xs">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={(p) => setParam({ page: String(p) })} />
          </>
        )}
      </Card>

      {/* Bulk shipment modal */}
      <Modal
        open={bulkOpen}
        title="Ship selected orders"
        onClose={() => {
          if (bulkBusy) return;
          setBulkOpen(false);
        }}
      >
        {bulkResult ? (
          <div className="space-y-3 text-sm">
            <p>
              <span className="font-semibold text-ok">{bulkResult.succeeded} shipped</span>
              {bulkResult.failed > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-bad">{bulkResult.failed} failed</span>
                </>
              )}
            </p>
            <ul className="max-h-60 space-y-1.5 overflow-y-auto">
              {bulkResult.results.map((r) => (
                <li
                  key={r.order_id}
                  className={`flex items-center justify-between gap-3 ${r.success ? 'text-ok' : 'text-bad'}`}
                >
                  <span>{shortId(r.order_id)} — {r.success ? `AWB ${r.awb_number}` : r.error}</span>
                  {r.success && r.label_url && (
                    <a
                      href={r.label_url}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 font-medium text-gold underline underline-offset-2"
                    >
                      Label
                    </a>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-3">
              {bulkResult.results.some((r) => r.success && r.shiprocket_shipment_id) && (
                <button className="btn-outline" onClick={downloadAllLabels} disabled={downloadingLabels}>
                  {downloadingLabels && <Spinner />}
                  Download all labels
                </button>
              )}
              <button className="btn-gold" onClick={() => setBulkOpen(false)}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {bulkError && (
              <div className="rounded-xl border border-bad/40 bg-bad/10 px-4 py-3 text-bad">
                {bulkError}
              </div>
            )}
            <p className="text-sub">
              Create a shipment for {selected.size} order{selected.size === 1 ? '' : 's'} using
              auto-assign (cheapest courier per order). Each gets its own AWB and label and moves to
              “Ready to ship”.
            </p>
            <div className="flex justify-end gap-3">
              <button className="btn-outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>
                Cancel
              </button>
              <button className="btn-gold" onClick={runBulkShip} disabled={bulkBusy}>
                {bulkBusy && <Spinner />}
                Ship {selected.size}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
